import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import echarts from 'echarts/lib/echarts'
import i18n from 'simple-react-i18n'
import { round } from 'lodash'
import { Card, CardContent, Grid } from '@mui/material'
import { getFullDate } from '../../../utils/DateUtil'
import { exportPictureIcon } from '../../../components/echart/EChartUtils'
import { ControlChartTabs, J365, useSimpleChartTabs } from '../../../components/echart/SimpleChartTabs'
import { CardTitle } from '../../../components/card/NewCard'
import MessageCard from '../../../components/card/MessageCard'
import Checkbox from '../../../components/forms/Checkbox'
import ReturnPeriodLegend from './ReturnPeriodLegend'
import { getVirtualStationBandSeries, isVirtualStationBand, useVirtualStation } from './VirtualStationUtils'
import { INDICATOR_OBJECT_TYPE, MAX_SCORE, SCORE_AXIS } from '../../../import/constants/IndicatorsConstants'

const GRAPH_HEIGHT = 300

const WaterStressChartPanel = ({ objectId, objectType = INDICATOR_OBJECT_TYPE.RESOURCE, title }) => {
    const { time, setTime, startDate, endDate } = useSimpleChartTabs(J365, 'WATER_STRESS')
    const { virtualStation, isLoaded, smoothed, setSmoothed } = useVirtualStation(objectId, startDate, endDate, objectType)

    const [hasIndicator, setHasIndicator] = useState(false)

    const measures = virtualStation.measures ?? []

    useEffect(() => {
        if (measures.length) {
            setHasIndicator(true)
        }
    }, [measures.length])

    if (!hasIndicator) {
        return null
    }

    const options = {
        series: [
            ...getVirtualStationBandSeries(virtualStation.bands, 0),
            {
                name: i18n.virtualStation,
                type: 'line',
                data: measures.map(([date, value]) => ({ value: [date, value] })),
                showSymbol: false,
                connectNulls: false,
                color: '#1d3b6e',
                lineStyle: { width: 2 },
            },
        ],
        legend: { show: false },
        xAxis: [{
            type: 'time',
            boundaryGap: true,
            axisLine: { show: true },
            axisTick: { show: true },
            splitLine: { show: true },
        }],
        yAxis: [{
            type: 'value',
            nameLocation: 'middle',
            name: `${i18n.waterStress} [/${MAX_SCORE}]`,
            nameGap: 40,
            ...SCORE_AXIS,
        }],
        tooltip: {
            trigger: 'axis',
            formatter: params => {
                const points = params.filter(o => !isVirtualStationBand(o.seriesName))
                if (!points.length) {
                    return ''
                }
                return `${getFullDate(points[0].value[0])} <br /> ${points.map(o => `${o.marker} ${o.seriesName} : ${round(o.value[1], 1)}`).join('<br />')}`
            },
        },
        grid: { top: 30, bottom: 70, left: '70px', right: '40px' },
        dataZoom: [{
            type: 'slider',
            filterMode: 'none',
        }, {
            type: 'inside',
            filterMode: 'filter',
        }],
        toolbox: {
            top: 5,
            right: 40,
            showTitle: false,
            itemSize: 18,
            tooltip: { show: true, position: 'bottom' },
            feature: {
                saveAsImage: {
                    title: i18n.pictureExport,
                    icon: exportPictureIcon,
                    name: i18n.waterStress,
                },
                restore: { title: i18n.restore },
            },
        },
        backgroundColor: '#fff',
    }

    return (
        <Card>
            <CardTitle title={title ?? i18n.waterStress} />
            <CardContent>
                <Grid container spacing={1}>
                    <Grid size={12}>
                        <ControlChartTabs
                            time={time}
                            setTime={setTime}
                            disabled={!isLoaded}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Checkbox
                            checked={smoothed}
                            label={i18n.weeklySmoothing}
                            onChange={setSmoothed}
                        />
                    </Grid>
                    <Grid size={12}>
                        {measures.length > 0 && (
                            <ReactECharts
                                echarts={echarts}
                                option={options}
                                notMerge
                                lazyUpdate
                                style={{ height: GRAPH_HEIGHT }}
                            />
                        )}
                        {measures.length === 0 && (<MessageCard>{i18n.noDataToDisplay}</MessageCard>)}
                    </Grid>
                    {measures.length > 0 && (
                        <Grid size={12}>
                            <ReturnPeriodLegend />
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    )
}

WaterStressChartPanel.propTypes = {
    objectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    objectType: PropTypes.number,
    title: PropTypes.string,
}

export default WaterStressChartPanel